import { Alert, Badge, Card, InfoTip } from '@zarya/ui';
import { dataCurta } from '@app/format';
import { errorView } from '@app/errors';
import { t } from '@app/i18n';
import { ReadingNote, readingView } from '@app/reading';
import { useIdentitySpiffe } from '../Identities.api';
import { commonName } from './distinguishedName';
import { atOrOn, shortWhen } from './renewalText';
import { TabState } from './TabState';
import s from '../Identities.module.css';

/**
 * Aba **SPIFFE / SPIRE** de T5 (FR-047, FR-054, FR-055).
 *
 * Dois blocos, deliberadamente separados (ajuste de aderência): **Derivado** é o
 * que a Zarya calcula do trust domain + namespace + service account; **Lido do
 * cluster** é o que o middleware conseguiu ler de verdade — certificado, bundle
 * e volume de identidade por pod. Quando o middleware não expõe essa leitura, o
 * bloco diz por quê e não finge dado.
 *
 * Desde a foto do runtime (aim-6ph.13, D12) o bloco **Lido do cluster** vem da foto
 * `identity.spiffe`, com a hora no subtítulo («observado pelo middleware · lido às
 * 14:05»). A aba não lê o executor: quem lê de novo é a gaveta, ao abrir.
 *
 * Desde o aim-ivi.1 (protótipo §2, decisões 5 e 6) cada pod mostra o que o helper de
 * identidade registrou no log: o SPIFFE ID, a última renovação e a validade. O serial e
 * o emissor do certificado do pod saíram, porque só a permissão de exec os lia. «Visto
 * no gateway» é da identidade, e some sem chamada no histórico (decisão 3).
 *
 * Regra do mrolim de 11/10/2026: nenhum texto diz como o valor foi obtido, nada que faça
 * o cliente duvidar se o que vê é real. Por isso a validade é «Validade: até 22:16», sem o
 * «calculada» nem a conta da renovação (a D5 mudou).
 */

export interface SpiffeTabProps {
  identityId: string;
  ativa: boolean;
  /** A gaveta pediu a leitura da identidade e o pedido não voltou: o bloco já diz «lendo». */
  readingPending?: boolean;
}

export function SpiffeTab({ identityId, ativa, readingPending = false }: SpiffeTabProps) {
  const spiffe = useIdentitySpiffe(identityId, ativa);
  const wasRead = spiffe.data?.read_from_cluster;
  const pods = wasRead?.pods ?? [];
  const reading = spiffe.data?.reading;
  const unavailable = spiffe.data?.unavailable_code;
  const seenAtGateway = spiffe.data?.seen_at_gateway ?? null;

  return (
    <TabState
      carregando={spiffe.isLoading}
      erro={spiffe.isError ? spiffe.error : null}
      tituloErro={t('identities.spiffeTab.error')}
      onRetry={() => void spiffe.refetch()}
      rotulo={t('identities.spiffeTab.loading')}
    >
      <Card
        title={
          <>
            {t('identities.spiffeTab.derived.title')}{' '}
            <InfoTip text={t('identities.spiffeTab.derived.tip')} size="xs" />
          </>
        }
        subtitle={t('identities.spiffeTab.derived.subtitle')}
      >
        <dl className={s.leitura}>
          <dt>{t('identities.spiffeTab.trustDomain')}</dt>
          <dd className={s.mono}>
            {spiffe.data?.derived?.trust_domain ?? <span className={s.vazio}>—</span>}
          </dd>
          <dt>{t('identities.spiffeTab.predicted')}</dt>
          <dd className={s.mono}>
            {spiffe.data?.derived?.spiffe_id_predicted ?? (
              <span className={s.vazio}>{t('identities.spiffeTab.predicted.none')}</span>
            )}
          </dd>
        </dl>
      </Card>

      <Card
        title={
          <>
            {t('identities.spiffeTab.read.title')}{' '}
            <InfoTip text={t('identities.spiffeTab.read.tip')} size="xs" />
          </>
        }
        subtitle={
          <>
            {t('identities.spiffeTab.read.subtitle')}
            {/* O « · » é pontuação, e a frase da foto só entra quando há o que dizer (protótipo, §3.1). */}
            {readingView(reading, { pending: readingPending })?.text ? ' · ' : null}
            <ReadingNote reading={reading} pending={readingPending} testId="spiffe-reading" />
          </>
        }
        className={s.cardEmpilhado}
      >
        {!wasRead ? (
          // Sem foto, o aviso é só da FALHA da primeira leitura, com o código que ela gravou. Nunca lido sem falha
          // (D, lendo pela primeira vez) não é «rota ausente»: o subtítulo diz «ainda não lido · lendo o cluster…», e
          // o bloco Derivado acima já aparece na hora (aim-6ph.13, §3.1).
          unavailable ? (
            <Alert tone="warning">{errorView(unavailable).texto}</Alert>
          ) : null
        ) : (
          <>
            {wasRead.diverges_from_expected ? (
              <Alert tone="danger" title={t('identities.spiffeTab.diverges.title')}>
                {t('identities.spiffeTab.diverges.message')}
              </Alert>
            ) : null}

            <dl className={s.leitura}>
              <dt>{t('identities.spiffeTab.expected')}</dt>
              <dd className={s.mono}>
                {wasRead.spiffe_id_expected ?? <span className={s.vazio}>—</span>}
              </dd>
              <dt>{t('identities.spiffeTab.bundle')}</dt>
              <dd>
                {wasRead.bundle ? (
                  <>
                    {/* Só o CN e a data (decisão de 04/10): o nome real começa com o
                        número de série da CA pelo OID, opaco e longo. O nome inteiro
                        fica no `title`, para quem precisa conferir. */}
                    <span className={s.mono} title={wasRead.bundle.issuer}>
                      {commonName(wasRead.bundle.issuer ?? '') ?? wasRead.bundle.issuer}
                    </span>
                    <span className={s.subTexto}>
                      {' '}
                      {t('identities.spiffeTab.bundle.validUntil', {
                        when: dataCurta(wasRead.bundle.not_after) ?? '—',
                      })}
                    </span>
                  </>
                ) : (
                  <span className={s.vazio}>{t('identities.spiffeTab.bundle.none')}</span>
                )}
              </dd>
              {seenAtGateway ? (
                <>
                  <dt>{t('identities.spiffeTab.seenAtGateway')}</dt>
                  <dd data-testid="spiffe-seen-at-gateway" data-at={seenAtGateway}>
                    {atOrOn(seenAtGateway)}
                  </dd>
                </>
              ) : null}
            </dl>

            {pods.length === 0 ? (
              <p className={s.vazio}>{t('identities.spiffeTab.pods.none')}</p>
            ) : (
              <ul className={s.lista}>
                {pods.map((pod) => (
                  <li key={pod.name} className={s.podItem} data-testid={`spiffe-pod-${pod.name}`}>
                    <span className={s.podCabecalho}>
                      <span className={s.monoForte}>{pod.name}</span>
                      {pod.svid_volume ? (
                        <Badge tone="success">{t('identities.spiffeTab.pod.volumeOn')}</Badge>
                      ) : (
                        <Badge tone="warning">{t('identities.spiffeTab.pod.volumeOff')}</Badge>
                      )}
                    </span>
                    {pod.renewal ? (
                      <dl className={s.leituraCompacta}>
                        <dt>{t('identities.spiffeTab.pod.spiffeId')}</dt>
                        <dd className={s.mono}>
                          {pod.renewal.spiffe_id}
                          {pod.spiffe_id_differs ? (
                            <>
                              {' '}
                              <span data-testid="spiffe-pod-id-differs">
                                <Badge tone="warning">{t('identities.spiffeTab.pod.spiffeIdDiffers')}</Badge>
                              </span>
                            </>
                          ) : null}
                        </dd>
                        <dt>{t('identities.spiffeTab.pod.renewedAt')}</dt>
                        <dd data-testid="spiffe-pod-renewed-at" data-at={pod.renewal.renewed_at}>
                          {atOrOn(pod.renewal.renewed_at)}
                        </dd>
                        <dt>{t('identities.spiffeTab.pod.validUntil')}</dt>
                        <dd data-testid="spiffe-pod-valid-until" data-at={pod.renewal.valid_until ?? undefined}>
                          {pod.renewal.valid_until
                            ? t('identities.spiffeTab.pod.validUntil.value', {
                                when: shortWhen(pod.renewal.valid_until),
                              })
                            : t('identities.spiffeTab.pod.validUntil.unknown')}
                        </dd>
                      </dl>
                    ) : pod.svid_volume ? (
                      // Sem volume, o badge do cabeçalho já diz tudo (protótipo §2.2, caso D).
                      <p className={s.subTexto} data-testid="spiffe-pod-no-renewal">
                        {pod.renewal_reason ?? t('identities.spiffeTab.pod.noRenewal')}
                      </p>
                    ) : null}
                    {pod.helper_last_error ? (
                      <p className={s.subTexto} data-testid="spiffe-pod-helper-error">
                        {t('identities.spiffeTab.pod.helperError', { error: pod.helper_last_error })}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Card>
    </TabState>
  );
}
